import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import { hopPose } from "./critterHop";
import { hoverShrink } from "./blobShadow";
import { ACT_TIME, ARRIVED, BIRD_CLIMB, BIRD_FLEE_SPEED, BIRD_FLUTTER_OVER, CELL_JITTER, CRITTER_SCALE, CritterKind, CritterWorld, EAR_BACK, EAR_FLAT, EAR_FLICK, EAR_FLICK_EVERY, EAR_UP, FLEE_FADE_RATE, FLEE_RANGE, FLUTTER_HEIGHT, FLUTTER_TIME, GAIT, GROW_RATE, IdleAct, LOOK_SWEEP, MOVE_ON, Mode, NIBBLE_CHEW, NIBBLE_HEAD, POSE_RATE, SHADOW, SHADOW_LIFT, SIT_UP, TURN_RATE, between, critterGround, damp, dampAngle } from "./critterTuning";
import { CritterBody, HIND_PIVOT, buildBird, buildRabbit } from "./critterBody";

/** One rabbit or bird (critters.ts CritterViews and critterHub.ts own them): its body, gait and idle life. */
export class Critter {
  /** Where the posture pivots, along the body (z): the hind feet, so sitting up rocks back onto them. */
  static readonly HindPivot = HIND_PIVOT;
  readonly root = new THREE.Group();
  /** Contact shadow, a sibling of the root in the views' group (it must not squash, lift or tilt with the body). */
  readonly shadow: THREE.Mesh;
  /** Pivots on the hind feet: the hop's squash and pitch, and sitting up. */
  private readonly posture = new THREE.Group();
  /** Pivots at the neck: nibbling, pecking and looking round. */
  private readonly head = new THREE.Group();
  private readonly wings: THREE.Object3D[] = [];
  private readonly ears: THREE.Object3D[] = [];
  private readonly earFlick: number[] = [];
  private readonly earFlickIn: number[] = [];
  private readonly feet: THREE.Object3D[] = [];
  /** Each hind foot's resting z, for the kick-back in the air. */
  private readonly footZ: number[] = [];
  private mode: Mode = "idle";
  private act: IdleAct = "still";
  private actFor = 0;
  private hopT = -1;                                  // < 0: feet down, not hopping
  private hopTime = 0.3;
  private hopHeight = 0.06;
  private flutter = false;
  private restIn = 0;
  private readonly from = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly fleeDir = new THREE.Vector3();
  private yaw = Math.random() * Math.PI * 2;
  private yawTo = this.yaw;
  private fade = 1;
  private clock = Math.random() * 7;
  private readonly pose = hopPose(0, 0);
  // blended posture (the activity's target is approached, never snapped to)
  private pitch = 0;
  private headPitch = 0;
  private headYaw = 0;
  private lift = 0;
  gone = false;
  cell: Cell;
  /** Grown-in scale (CritterViews.Scale). */
  private readonly size: number;

  constructor(readonly kind: CritterKind, cell: Cell, shadowMat: THREE.Material, shadowGeo: THREE.BufferGeometry) {
    this.cell = cell;
    this.size = CRITTER_SCALE[kind];
    this.root.add(this.posture);
    const body: CritterBody = { root: this.root, posture: this.posture, head: this.head, wings: this.wings, ears: this.ears, earFlick: this.earFlick, earFlickIn: this.earFlickIn, feet: this.feet, footZ: this.footZ };
    if (kind === "rabbit") buildRabbit(body); else buildBird(body);
    const x = cell.x + (Math.random() - 0.5) * CELL_JITTER, z = cell.y + (Math.random() - 0.5) * CELL_JITTER;
    this.root.position.set(x, critterGround(x, z), z);
    this.target.copy(this.root.position);
    this.root.rotation.y = this.yaw;
    this.root.scale.setScalar(0.001);                    // grows in
    this.shadow = new THREE.Mesh(shadowGeo, shadowMat);
    this.shadow.renderOrder = 1;
    this.shadow.castShadow = false;
    this.shadow.receiveShadow = false;
    this.shadow.scale.setScalar(0.001);
    this.startIdle();
    this.actFor = 0.6 + Math.random() * 1.5;
    this.finish(0);
  }

  /**
   * Frees the body's geometry and materials, all built for this critter alone (the shadow's are the owner's, shared).
   * Left to the collector, the renderer's attribute bindings kept them and their GPU buffers — every hub visit added
   * its rabbits and bird for good (RC1 FX-C).
   */
  dispose(): void {
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.geometry.dispose();
      for (const x of Array.isArray(m.material) ? m.material : [m.material]) x.dispose();
    });
  }

  /** Wings: folded down the flanks, or beating. */
  private setWings(beat: number | null): void {
    this.wings.forEach((w, i) => {
      const side = i === 0 ? -1 : 1;
      w.rotation.z = beat === null ? -side * Critter.WingFold : side * (0.3 + beat * 0.8);
    });
  }
  /** Folded wing angle, radians down from level. */
  static readonly WingFold = 1.15;

  private startIdle(): void {
    this.mode = "idle";
    const r = Math.random();
    this.act = r < 0.5 ? "nibble" : r < 0.8 ? "look" : "still";
    this.actFor = between(ACT_TIME[this.act]);
    if (this.act === "look") this.yawTo += (Math.random() - 0.5) * 2 * Critter.LookTurn;   // shuffles round to look
  }
  /** Most a critter turns on the spot before looking round, radians either way. */
  static readonly LookTurn = 0.7;

  /** Sets off for `to` (already on the ground), hop by hop. */
  private travelTo(to: THREE.Vector3): void {
    this.mode = "travel";
    this.target.copy(to);
    this.restIn = 0;
  }

  private startHop(len: number, time: number, height: number, dirX: number, dirZ: number): void {
    const p = this.root.position;
    this.from.set(p.x, 0, p.z);
    this.to.set(p.x + dirX * len, 0, p.z + dirZ * len);
    this.hopT = 0; this.hopTime = time; this.hopHeight = height;
    this.yawTo = Math.atan2(dirX, dirZ);
  }

  update(dt: number, enemies: readonly THREE.Vector3[], threatened: boolean, world: CritterWorld): void {
    this.clock += dt;
    const p = this.root.position;
    // grow in / fade out
    if (this.mode !== "flee") this.root.scale.setScalar(Math.min(this.size, this.root.scale.x + dt * GROW_RATE * this.size));
    else {
      this.fade -= dt * FLEE_FADE_RATE;
      if (this.fade <= 0) { this.gone = true; return; }
      this.root.scale.setScalar(Math.max(0.001, this.fade * this.size));
    }
    // a threat nearby, or the wave starting, sends it running
    if (this.mode !== "flee") {
      let nearest: THREE.Vector3 | null = null; let best = FLEE_RANGE;
      for (const e of enemies) { const d = e.distanceTo(p); if (d < best) { best = d; nearest = e; } }
      if (threatened || nearest) {
        this.mode = "flee";
        const away = nearest ? p.clone().sub(nearest) : new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5);
        away.y = 0; if (away.lengthSq() < 0.001) away.set(1, 0, 0);
        this.fleeDir.copy(away.normalize());
        this.yawTo = Math.atan2(this.fleeDir.x, this.fleeDir.z);
        if (this.hopT < 0 && this.kind === "rabbit") this.startHop(GAIT.rabbit.fleeHop, GAIT.rabbit.fleeTime, GAIT.rabbit.fleeHeight, this.fleeDir.x, this.fleeDir.z);
      }
    }
    if (this.mode === "flee" && this.kind === "bird") {
      // takes off: up and away, wings beating fast — flying, so off the ground on purpose
      p.addScaledVector(this.fleeDir, dt * BIRD_FLEE_SPEED);
      this.lift += dt * BIRD_CLIMB;
      p.y = critterGround(p.x, p.z) + this.lift;
      this.setWings(Math.sin(this.clock * 40));
      this.yaw = dampAngle(this.yaw, this.yawTo, TURN_RATE, dt);
      this.finish(dt);
      return;
    }
    if (this.hopT >= 0) this.hop(dt);
    else if (this.mode === "flee") this.startHop(GAIT.rabbit.fleeHop, GAIT.rabbit.fleeTime, GAIT.rabbit.fleeHeight, this.fleeDir.x, this.fleeDir.z);
    else if (this.mode === "travel") this.travel(dt);
    else this.idle(dt, world);
    this.finish(dt);
  }

  /** One frame of the current hop (or bird flutter): the feet follow the drawn ground under the arc. */
  private hop(dt: number): void {
    this.hopT = Math.min(1, this.hopT + dt / this.hopTime);
    const pose = hopPose(this.hopT, this.hopHeight, this.pose);
    const p = this.root.position;
    p.x = this.from.x + (this.to.x - this.from.x) * pose.along;
    p.z = this.from.z + (this.to.z - this.from.z) * pose.along;
    this.lift = pose.lift;
    if (this.flutter) {
      this.pitch = 0;
      this.posture.scale.set(1, 1, 1);
      this.setWings(pose.airborne ? Math.sin(this.clock * 30) : null);
    } else {
      this.pitch = pose.pitch;
      const sy = 1 + pose.squash, sxz = 1 / Math.sqrt(sy);   // squash and stretch keep the volume
      this.posture.scale.set(sxz, sy, sxz);
      this.kickFeet(pose.legs);
      if (this.kind === "bird") this.setWings(null);
    }
    this.headPitch = damp(this.headPitch, 0, POSE_RATE, dt);
    this.headYaw = damp(this.headYaw, 0, POSE_RATE, dt);
    const ears = this.mode === "flee" ? EAR_FLAT : pose.airborne ? EAR_BACK : EAR_UP;
    for (const e of this.ears) e.rotation.x = damp(e.rotation.x, ears, POSE_RATE * 2, dt);
    if (this.hopT >= 1) {
      this.hopT = -1; this.flutter = false; this.lift = 0;
      this.posture.scale.set(1, 1, 1);
      this.kickFeet(0);
      this.restIn = between(GAIT[this.kind].rest);
    }
  }
  /** Hind feet kicked out behind at take-off (`legs` 0 → 1): toes down, reaching back. Birds' legs just hang. */
  private kickFeet(legs: number): void {
    if (this.kind !== "rabbit") return;
    this.feet.forEach((f, i) => { f.rotation.x = legs * Critter.LegKick; f.position.z = this.footZ[i] - legs * Critter.LegReach; });
  }
  /** How far the hind feet swing (radians) and reach back (model units) at take-off. */
  static readonly LegKick = 0.7;
  static readonly LegReach = 0.035;

  private travel(dt: number): void {
    this.settle(dt, 0, 0, 0);
    this.restIn -= dt;
    if (this.restIn > 0) return;
    const p = this.root.position;
    const dx = this.target.x - p.x, dz = this.target.z - p.z, d = Math.hypot(dx, dz);
    if (d < ARRIVED) { this.startIdle(); return; }
    const g = GAIT[this.kind];
    if (this.kind === "bird" && d > BIRD_FLUTTER_OVER) {
      this.startHop(d, FLUTTER_TIME, FLUTTER_HEIGHT, dx / d, dz / d);
      this.flutter = true;
      return;
    }
    const len = Math.min(d, g.hop * (0.85 + Math.random() * 0.3));
    this.startHop(len, g.time, g.height, dx / d, dz / d);
  }

  /** On the ground between moves: nibbling, sitting up to look round, or still, with the ears flicking. */
  private idle(dt: number, world: CritterWorld): void {
    this.actFor -= dt;
    const rabbit = this.kind === "rabbit";
    switch (this.act) {
      case "nibble": {
        // rabbit: head down and chewing; bird: pecking in quick jabs
        const chew = rabbit ? NIBBLE_HEAD + Math.sin(this.clock * 14) * NIBBLE_CHEW : Math.max(0, Math.sin(this.clock * 7)) * 0.9;
        this.settle(dt, rabbit ? 0.08 : 0, chew, 0);
        break;
      }
      case "look": {
        // rabbit: up on the haunches, head sweeping; bird: head jerking between glances
        const sweep = rabbit ? Math.sin(this.clock * 1.3) * LOOK_SWEEP : (Math.floor(this.clock * 2.5) % 3 - 1) * LOOK_SWEEP;
        this.settle(dt, rabbit ? SIT_UP : 0, 0, sweep);
        break;
      }
      default: this.settle(dt, 0, 0, 0);
    }
    if (this.actFor > 0) return;
    const next = Math.random() < MOVE_ON ? world.nextSpot(this.cell) : null;
    if (next) { this.cell = next.cell; this.travelTo(next.at); }
    else this.startIdle();
  }

  /** Blends the posture toward a resting pose: body pitch, head nod and head turn; the ears flick now and then. */
  private settle(dt: number, pitch: number, headPitch: number, headYaw: number): void {
    this.pitch = damp(this.pitch, pitch, POSE_RATE, dt);
    this.headPitch = damp(this.headPitch, headPitch, POSE_RATE * 1.5, dt);
    this.headYaw = damp(this.headYaw, headYaw, POSE_RATE, dt);
    this.lift = 0;
    const breathe = 1 + Math.sin(this.clock * Critter.BreathRate) * Critter.Breath;
    this.posture.scale.set(1, breathe, 1);
    if (this.kind === "bird") this.setWings(null);
    const up = this.act === "look" ? EAR_UP + 0.1 : EAR_UP;
    this.ears.forEach((e, i) => {
      this.earFlickIn[i] -= dt;
      if (this.earFlickIn[i] <= 0) { this.earFlick[i] = 1; this.earFlickIn[i] = between(EAR_FLICK_EVERY); }
      this.earFlick[i] = Math.max(0, this.earFlick[i] - dt * 6);
      e.rotation.x = damp(e.rotation.x, up - Math.sin(this.earFlick[i] * Math.PI) * EAR_FLICK, POSE_RATE * 2, dt);
    });
  }
  /** Idle breathing: y scale amplitude and rate (rad/s). */
  static readonly Breath = 0.018;
  static readonly BreathRate = 5;

  /** Writes the frame's pose: feet on the drawn ground (plus any hop), yaw, posture, and the contact shadow under it. */
  private finish(dt: number): void {
    const p = this.root.position;
    const ground = critterGround(p.x, p.z);
    p.y = ground + this.lift;
    this.yaw = dampAngle(this.yaw, this.yawTo, TURN_RATE, dt);
    this.root.rotation.y = this.yaw;
    this.posture.rotation.x = this.pitch;
    this.head.rotation.set(this.headPitch, this.headYaw, 0, "YXZ");
    const [w, l] = SHADOW[this.kind];
    const s = this.root.scale.x * hoverShrink(this.lift);
    this.shadow.position.set(p.x, ground + SHADOW_LIFT, p.z);
    this.shadow.rotation.y = this.yaw;
    this.shadow.scale.set(w * s, 1, l * s);
  }
}
